import { createAsyncThunk, nanoid } from "@reduxjs/toolkit";
import API from "../../utils/axiosInstance";
import { Client, ClientsResponse, CompleteSSO } from "./types";
import {
  setGrantedClients,
  setNotGrantedClients,
  setClients,
} from "./microsoftSlice";
import { RootState } from "..";
import { setAlert } from "../alerts/alertSlice";
// import { RootState } from '../../model';

export const updateClient = async (
  clientId: string,
  tenantId: string
): Promise<CompleteSSO> => {
  const response = await API.patch<CompleteSSO>(`/pii-protect/client`, {
    clientId,
    tenantId,
  });
  return response.data;
};

export const connectPiiProtectAPI = async (
  clientId: string,
  tenantId: string
): Promise<CompleteSSO> => {
  const response = await API.post<CompleteSSO>(
    `/pii-protect/client/connect-pii-protect`,
    { clientId, tenantId }
  );
  return response.data;
};

export const disconnectPiiProtectAPI = async (
  clientId: string,
  tenantId: string
): Promise<CompleteSSO> => {
  const response = await API.post<CompleteSSO>(
    `/pii-protect/client/disconnect-pii-protect`,
    { clientId, tenantId }
  );
  return response.data;
};

export const refreshPiiProtectAPI = async (
  clientId: string
): Promise<CompleteSSO> => {
  const response = await API.get<CompleteSSO>(`/pii-protect/sync?clientId=${clientId}`);
  return response.data;
};

export const fetchAllClients = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>("microsoft/fetchAllClients", async (_, { dispatch, rejectWithValue }) => {
  try {
    const { data } = await API.get<ClientsResponse>("/clients");
    const { message, result } = data;
    if (!message || !Array.isArray(result)) {
      throw new Error("Malformed response");
    }
    dispatch(setClients(result));
  } catch (e: any) {
    return rejectWithValue(e.message || "Failed to fetch clients");
  }
});

export const completeSSOOnboarding = createAsyncThunk<
  string,
  { clientId: string; tenantId: string },
  { rejectValue: string }
>(
  "microsoft/completeSSO",
  async ({ clientId, tenantId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateClient(clientId, tenantId);
      const { message, result } = response;

      if (!message || !result) throw new Error("Client does not exist");

      const state = getState() as RootState;
      const { notGrantedConsentClients = [], grantedConsentClients = [] } =
        state.microsoft;
      const already = grantedConsentClients.some(
        (c) => c.clientId === clientId
      );
      if (!already) {
        dispatch(setGrantedClients([...grantedConsentClients, result]));
      }

      dispatch(
        setNotGrantedClients(
          notGrantedConsentClients.filter((c) => c.clientId !== clientId)
        )
      );

      dispatch(
        setAlert({
          id: nanoid(),
          message,
          alertType: "success",
          isOpen: true,
        })
      );

      return message;
    } catch (err: any) {
      dispatch(
        setAlert({
          id: nanoid(),
          message: err.response?.data?.message || err.message,
          alertType: "danger",
          isOpen: true,
        })
      );
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const refreshPIIProtect = createAsyncThunk<
  string,
  { clientId: string },
  { rejectValue: string }
>(
  "microsoft/refreshPIIProtect",
  async ({ clientId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await refreshPiiProtectAPI(clientId);
      dispatch(
        setAlert({
          id: nanoid(),
          message: response.message,
          alertType: "success",
          isOpen: true,
        })
      );
      return response.message;
    } catch (error: any) {
      dispatch(
        setAlert({
          id: nanoid(),
          message: error.response?.data.message || error.message,
          alertType: "danger",
          isOpen: true,
        })
      );
      return rejectWithValue(error.response?.data.message || error.message);
    }
  }
);

export const connectPIIProtect = createAsyncThunk<
  string,
  { clientId: string; tenantId: string },
  { rejectValue: string }
>(
  "microsoft/connectPIIProtect",
  async ({ clientId, tenantId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { data } = await API.post<CompleteSSO>("/pii-protect/connect", {
        clientId,
        tenantId,
      });
      const { message, result } = data;
      if (!message || !result) throw new Error("Connect PII failed");

      const { clients } = (getState() as RootState).microsoft;
      const updatedClients: Client[] = (clients ?? []).map((c) =>
        c.clientId !== clientId
          ? c
          : {
              ...c,
              isPIIProtected: true,
              azureTenants: (c.azureTenants ?? []).map((t) =>
                t.azure_tenant_id === tenantId
                  ? { ...t, isPIIProtected: true }
                  : t
              ),
            }
      );
      dispatch(setClients(updatedClients));

      dispatch(
        setAlert({
          id: nanoid(),
          message,
          alertType: "success",
          isOpen: true,
        })
      );
      return message;
    } catch (err: any) {
      dispatch(
        setAlert({
          id: nanoid(),
          message: err.response?.data?.message || err.message,
          alertType: "danger",
          isOpen: true,
        })
      );
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const disconnectPIIProtect = createAsyncThunk<
  string,
  { clientId: string; tenantId: string },
  { rejectValue: string }
>(
  "microsoft/disconnectPIIProtect",
  async ({ clientId, tenantId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { data } = await API.post<CompleteSSO>("/pii-protect/disconnect", {
        clientId,
        tenantId,
      });
      const { message, result } = data;
      if (!message || !result) throw new Error("Disconnect PII failed");

      // update the unified clients array
      const { clients } = (getState() as RootState).microsoft;
      const updatedClients: Client[] = (clients ?? []).map((c) =>
        c.clientId !== clientId
          ? c
          : {
              ...c,
              isPIIProtected: false,
              azureTenants: (c.azureTenants ?? []).map((t) =>
                t.azure_tenant_id === tenantId
                  ? { ...t, isPIIProtected: false }
                  : t
              ),
            }
      );
      dispatch(setClients(updatedClients));

      dispatch(
        setAlert({
          id: nanoid(),
          message,
          alertType: "success",
          isOpen: true,
        })
      );
      return message;
    } catch (err: any) {
      dispatch(
        setAlert({
          id: nanoid(),
          message: err.response?.data?.message || err.message,
          alertType: "danger",
          isOpen: true,
        })
      );
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const confirmAdminConsent = createAsyncThunk<
  string,
  { clientId: string; tenantId: string },
  { rejectValue: string }
>(
  "microsoft/confirmAdminConsent",
  async ({ clientId, tenantId }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await API.patch<CompleteSSO>(
        "/clients/grant-admin-consent",
        {
          clientId: clientId,
          tenantId: tenantId,
          admin_consent: true,
        }
      );
      const { message, result } = data;
      if (!message || !result) throw new Error("Grant consent failed");

      dispatch(fetchAllClients());

      dispatch(
        setAlert({
          id: nanoid(),
          message,
          alertType: "success",
          isOpen: true,
        })
      );

      return message;
    } catch (err: any) {
      dispatch(
        setAlert({
          id: nanoid(),
          message: err.response?.data?.message || err.message,
          alertType: "danger",
          isOpen: true,
        })
      );
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);
